
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePersona } from '@/hooks/use-persona';
import { formatSuggestionTitle } from '@/utils/document-formatter';

export interface SmartSuggestion {
  id: string;
  title: string;
  description: string;
  action: () => void;
  priority: 'high' | 'medium' | 'low';
  category: string;
  personaRelevant: boolean;
}

export function useEnhancedSuggestions() {
  const [suggestions, setSuggestions] = useState<SmartSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { personaConfig } = usePersona();

  useEffect(() => {
    generatePersonaAwareSuggestions();
  }, [personaConfig]);

  const generatePersonaAwareSuggestions = async () => {
    try {
      setIsLoading(true);
      
      // Get user's business assessment
      const { data: assessment } = await supabase
        .from('business_assessments')
        .select('data')
        .single();

      // Get user's documents
      const { data: documents } = await supabase
        .from('documents')
        .select('type')
        .order('created_at', { ascending: false });

      const assessmentData = assessment?.data as any;
      const existingDocTypes = documents?.map(d => d.type) || [];

      // Generate persona-specific suggestions
      const personaBasedSuggestions = generatePersonaSuggestions(
        personaConfig,
        assessmentData,
        existingDocTypes
      );

      setSuggestions(personaBasedSuggestions);
    } catch (error) {
      console.error('Error generating suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const generatePersonaSuggestions = (
    persona: any,
    assessmentData: any,
    existingDocTypes: string[]
  ): SmartSuggestion[] => {
    const suggestions: SmartSuggestion[] = [];

    // Assessment-based suggestions
    if (!assessmentData) {
      suggestions.push({
        id: 'complete-assessment',
        title: `Complete Business Assessment for ${persona.id} Insights`,
        description: `Get personalized ${persona.title.toLowerCase()} recommendations`,
        action: () => window.location.href = '/business-assessment',
        priority: 'high',
        category: 'Setup',
        personaRelevant: true
      });
    }

    // Persona-specific document suggestions
    persona.documentTypes.forEach((docType: string) => {
      if (!existingDocTypes.includes(docType)) {
        suggestions.push({
          id: `create-${docType}`,
          title: `Create ${formatSuggestionTitle(docType)}`,
          description: `Essential for ${persona.title.toLowerCase()} success`,
          action: () => window.location.href = '/documents',
          priority: 'medium',
          category: persona.title,
          personaRelevant: true
        });
      }
    });

    // Role-specific strategic suggestions based on assessment
    if (assessmentData) {
      if (persona.id === 'CSO') {
        suggestions.push({
          id: 'market-analysis',
          title: 'Conduct Competitive Analysis',
          description: `Analyze ${assessmentData.industry} market positioning`,
          action: () => window.location.href = '/documents',
          priority: 'high',
          category: 'Strategy',
          personaRelevant: true
        });
      } else if (persona.id === 'CFO') {
        suggestions.push({
          id: 'financial-forecast',
          title: 'Build Financial Forecast',
          description: `Plan for ${assessmentData.annualRevenue} revenue growth`,
          action: () => window.location.href = '/documents',
          priority: 'high',
          category: 'Finance',
          personaRelevant: true
        });
      } else if (persona.id === 'CTO') {
        suggestions.push({
          id: 'tech-roadmap',
          title: 'Develop Technology Roadmap',
          description: 'Plan digital transformation initiatives',
          action: () => window.location.href = '/documents',
          priority: 'high',
          category: 'Technology',
          personaRelevant: true
        });
      } else if (persona.id === 'COO') {
        suggestions.push({
          id: 'process-optimization',
          title: 'Optimize Operations',
          description: `Streamline processes for ${assessmentData.employeeCount} team`,
          action: () => window.location.href = '/documents',
          priority: 'high',
          category: 'Operations',
          personaRelevant: true
        });
      } else if (persona.id === 'CPO') {
        suggestions.push({
          id: 'product-strategy',
          title: 'Define Product Strategy',
          description: 'Align product roadmap with market needs',
          action: () => window.location.href = '/documents',
          priority: 'high',
          category: 'Product',
          personaRelevant: true
        });
      }
    }

    return suggestions.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  };

  return {
    suggestions: suggestions.filter(s => s.personaRelevant),
    isLoading,
    refreshSuggestions: generatePersonaAwareSuggestions
  };
}
